import { Link, NavLink } from 'react-router';
import WebsiteLogo from '../WebsiteLogo';
import { SIDE_MENU } from '@/constants/sidemenu.constant';
import IconsComponent from '../icons/IconsComponent';
import { useUIStore } from '@/store/ui.store';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';
import IntujiLogoSrc from '@/assets/logos/intuji_logo.svg';
import { ROUTES } from '@/constants/route.constant';
import { PanelLeftOpen, PanelRightOpen } from 'lucide-react';

const SideMenu = () => {
  const { isSidebarCollapsed, toggleSidebar } = useUIStore();

  const isCollapsed = isSidebarCollapsed;

  return (
    <aside
      className={`fixed top-0 left-0 z-20 h-screen w-[50px] border-r border-solid border-white/5 transition-all duration-300 ${
        isCollapsed ? 'lg:w-[50px]' : 'lg:w-[min(16.5%,280px)]'
      }`}
    >
      <nav
        className={`text-stone flex h-full flex-col pt-8 pb-8 text-lg transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-4 xl:px-4'}`}
      >
        {/* Logo: intuji icon slides in from left on collapse; full logo slides in from right on expand */}
        <div className='relative mb-10 h-[30px] overflow-hidden'>
          <div
            className={`absolute inset-0 transition-transform duration-300 ${isCollapsed ? 'translate-x-full' : 'translate-x-0'}`}
          >
            <WebsiteLogo />
          </div>
          <div
            className={`absolute inset-0 transition-transform duration-300 ${isCollapsed ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <NavLink to={ROUTES.DASHBOARD.TIMESHEET}>
              <img
                src={IntujiLogoSrc}
                alt='Logo'
                style={{ width: '30px', height: '30px', display: 'block' }}
              />
            </NavLink>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <div className='flex h-full flex-col items-start justify-between gap-6'>
            <ul className='space-y-2'>
              {SIDE_MENU.main.map((item) => {
                return (
                  <li
                    key={item.label}
                    className={
                      item.isDisabled
                        ? 'pointer-events-none text-stone-600'
                        : ''
                    }
                  >
                    <Tooltip providerOptions={{ delayDuration: 200 }}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={item.route}
                          className={({ isActive }) =>
                            `group disabled:text-stone-light flex items-center justify-center gap-2 rounded-lg p-2 hover:text-white ${
                              isCollapsed
                                ? 'lg:justify-center'
                                : 'lg:justify-start xl:gap-3.5'
                            } ${
                              isActive
                                ? 'font-extrabold text-white'
                                : 'font-semibold'
                            }`
                          }
                        >
                          {({ isActive }) => {
                            const defaultFill = isActive ? 'white' : '#A6A09B';

                            return (
                              <>
                                <span className='shrink-0 group-hover:[&_path]:fill-white'>
                                  <IconsComponent
                                    icon={item.icon}
                                    {...(item.iconProps || {})}
                                    fill={
                                      item.isDisabled ? '#57534D' : defaultFill
                                    }
                                    size='md'
                                  />
                                </span>
                                <span
                                  className={`truncate ${
                                    isCollapsed
                                      ? 'hidden'
                                      : 'hidden lg:inline-block'
                                  }`}
                                >
                                  {item.label}
                                </span>
                              </>
                            );
                          }}
                        </NavLink>
                      </TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side='right'>
                          <p>{item.label}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
            <ul className='space-y-5'>
              {SIDE_MENU.footer.map((item) => (
                <li
                  key={item.label}
                  className={`last:text-stone-light ${item.isDisabled ? 'pointer-events-none text-stone-600' : ''}`}
                >
                  <Tooltip providerOptions={{ delayDuration: 200 }}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.route}
                        className={`group disabled:text-stone-light flex items-center justify-center gap-2 hover:text-white ${
                          isCollapsed
                            ? 'lg:justify-center'
                            : 'lg:justify-start xl:gap-3.5'
                        }`}
                      >
                        <span className='shrink-0 group-hover:[&_path]:fill-white'>
                          <IconsComponent
                            icon={item.icon}
                            fill={
                              item.isDisabled
                                ? '#57534D'
                                : item.iconProps?.fill || '#A6A09B'
                            }
                          />
                        </span>
                        <span
                          className={`truncate ${
                            isCollapsed ? 'hidden' : 'hidden lg:inline-block'
                          }`}
                        >
                          {item.label}
                        </span>
                      </Link>
                    </TooltipTrigger>
                    {isCollapsed && (
                      <TooltipContent side='right'>
                        <p>{item.label}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </li>
              ))}

              {/* Desktop collapse toggle */}
              <li className='hidden lg:block'>
                <button
                  onClick={toggleSidebar}
                  className='group flex w-full cursor-pointer items-center justify-start gap-2 hover:text-white'
                  aria-label={
                    isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                  }
                >
                  {isCollapsed ? (
                    <PanelLeftOpen
                      size={18}
                      className='text-[#A6A09B] group-hover:text-white'
                    />
                  ) : (
                    <PanelRightOpen
                      size={18}
                      className='text-[#A6A09B] group-hover:text-white'
                    />
                  )}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </aside>
  );
};

export default SideMenu;

